// Copyright ©️ 2026 eVolve MEP, LLC

import './activity-feed.css';

import { useRef, useEffect } from 'react';

import {
  formatDate,
  type RenderableUser,
  type ISOTimestamp,
  type WithOptional,
  type WrappedPaginatedGet,
  type EvolveApiRoute,
  type EvolveApiResponse,
} from '@evolvemep/api-ts';
import { Flex, Timeline, Text, Card } from '@mantine/core';

import { UserAvatar } from 'components/Mantine/UserInfo';
import { validateUserNameOrEmail } from 'helpers/userHelpers';
import { TriggerOnVisible } from 'hooks/useOnScreen';

type Props<Route extends EvolveApiRoute<'GET_PAGINATED'>> = {
  paginatedGet: WithOptional<WrappedPaginatedGet<Route>, 'setDefaultOpts'>;
  /** Used to map a paginated event response to an item on the activity feed */
  getActivityFeedItem: (event: EvolveApiResponse<'GET_PAGINATED', Route>['data'][number]) => {
    label: string;
    createdByUser: RenderableUser;
    createdOn: ISOTimestamp;
    comments?: string | null;
  };
  /**
   * When the state or status of an entity changes, the timeline needs to be refreshed.
   * If this value changes, the timeline will be refreshed.
   */
  currentStatus: string;
};

export const ActivityFeed = <Route extends EvolveApiRoute<'GET_PAGINATED'>>({
  paginatedGet,
  getActivityFeedItem,
  currentStatus,
}: Props<Route>) => {
  const { data, loading, fetchNextPage, reset } = paginatedGet;

  const previousStatus = useRef(currentStatus);

  useEffect(() => {
    if (currentStatus !== previousStatus.current) {
      reset();
      previousStatus.current = currentStatus;
    }
  }, [currentStatus, reset]);

  return (
    <Timeline
      className="activity-feed"
      lineWidth={2}
      bulletSize={20}
      styles={{
        root: {
          paddingBottom: 0,
          marginBottom: 0,
        },
        itemBullet: {
          backgroundColor: 'white',
          border: 'none',
        },
      }}
    >
      {data.map((event) => {
        const status = getActivityFeedItem(event);
        return (
          <Timeline.Item
            key={status.createdOn}
            bullet={<UserAvatar size={20} name={validateUserNameOrEmail(status.createdByUser)} />}
            title={
              <Flex>
                <Flex align="center" gap={6} mt={-2} fz="sm">
                  <Text fw={500}>{status.label}</Text>
                  <Text c="dimmed">•</Text>
                  <Text c="dimmed">{validateUserNameOrEmail(status.createdByUser)}</Text>
                </Flex>
              </Flex>
            }
          >
            {!!status.comments && (
              <Card bg="gray.0" p="xs">
                <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {status.comments}
                </Text>
              </Card>
            )}
            <Text c="dimmed" title={formatDate(status.createdOn, 'PPPppp')} fz="xs">
              {formatDate(status.createdOn, 'PPP')}
            </Text>
          </Timeline.Item>
        );
      })}
      <TriggerOnVisible onVisible={fetchNextPage} loading={loading} />
    </Timeline>
  );
};
